import { getMonthTwoDigits } from '../../utils/date';
import { RequestStatus } from '../../shared/Requests';
import React, { createContext, Reducer, useReducer, useState, useMemo, useCallback } from 'react';
import BillingAdvancedDailyEngagedUserServiceApi from '../../api/Billing/BillingAdvancedDailyEngagedUserApi';
import { CompiledAdvancedDailyEngagedUserConsumption } from '../../models/billing/advancedDailyEngagedUser/CompiledAdvancedDailyEngagedUserConsumption';
import { CompiledTotalAdvancedDailyEngagedUserConsumption } from '../../models/billing/advancedDailyEngagedUser/CompiledTotalAdvancedDailyEngagedUserConsumption';
import {
  BillingMetrics,
  getMetricStatus,
  MetricStatus,
  METRIC_NOT_REGISTERED,
  UNLIMITED_PLAN_METRIC,
} from '../../shared/BillingMetrics';
import FeatureToggleService from '../../services/FeatureToggleService';
import { DEU_AI_ENABLED } from '../../shared/FeatureToggleKeys';

interface CompiledAdvancedDailyEngagedUserDataState {
  compiledAdvancedDailyEngagedUserConsumption: CompiledAdvancedDailyEngagedUserConsumption[];
  compiledTotalAdvancedDailyEngagedUserConsumption: CompiledTotalAdvancedDailyEngagedUserConsumption;
  compiledContainsDataOnSelectedMonth: boolean;
}

interface AdvancedDeuConsumptionContextData {
  compiledAdvancedDailyEngagedUserDataState: CompiledAdvancedDailyEngagedUserDataState;
  consolidatedAdvancedDeuApiRequisition: (
    dateToSearch: Date,
    hasEnabledMetric: (value: Array<BillingMetrics>) => boolean,
  ) => Promise<void>;
  isAdvancedDeuLoading: boolean;
  advancedDeuStatus: MetricStatus;
  advancedDeuMetricIds: Array<BillingMetrics>;
  isDeuAiEnabled: boolean;
}

interface Props {
  tenant: any;
}

export const AdvancedDeuConsumptionContext = createContext<AdvancedDeuConsumptionContextData>(
  {} as AdvancedDeuConsumptionContextData,
);

const initialDailyConsumption = new CompiledAdvancedDailyEngagedUserConsumption(
  new CompiledAdvancedDailyEngagedUserConsumption(null),
);
const initialTotalConsumption = new CompiledTotalAdvancedDailyEngagedUserConsumption(
  new CompiledTotalAdvancedDailyEngagedUserConsumption(null),
);

export const AdvancedDeuConsumptionContextProvider: React.FC<Props> = ({ children, tenant }) => {
  const [isAdvancedDeuLoading, setIsAdvancedDeuLoading] = useState(false);
  const [advancedDeuStatus, setAdvancedDeuStatus] = useState(MetricStatus.error);
  const [isDeuAiEnabled, setIsDeuAiEnabled] = useState(false);
  
  const billingAdvancedDailyEngagedUserApi = useMemo(() => new BillingAdvancedDailyEngagedUserServiceApi(), []);

  const advancedDeuMetricIds = (tenant?.subscription || []).filter(
        (item) =>
          item?.metricId === BillingMetrics.userConversation ||
          item?.metricId === BillingMetrics.prospectConversation ||
          item?.metricId === BillingMetrics.activeMessage ||
          item?.metricId === BillingMetrics.dailyEngagedUser ||
          item?.metricId === BillingMetrics.dailyEngagedUserAI
      )
      .map((item) => item.metricId);

  const [compiledAdvancedDailyEngagedUserDataState, setCompiledAdvancedDailyEngagedUserDataState] = useReducer<
    Reducer<CompiledAdvancedDailyEngagedUserDataState, Partial<CompiledAdvancedDailyEngagedUserDataState>>
  >(
    (compiledAdvancedDailyEngagedUserDataState, newCompiledAdvancedDailyEngagedUserDataState) => ({
      ...compiledAdvancedDailyEngagedUserDataState,
      ...newCompiledAdvancedDailyEngagedUserDataState,
    }),
    {
      compiledAdvancedDailyEngagedUserConsumption: [initialDailyConsumption],
      compiledTotalAdvancedDailyEngagedUserConsumption: initialTotalConsumption,
      compiledContainsDataOnSelectedMonth: false,
    },
  );

  const consolidatedAdvancedDeuApiRequisition = useCallback(async (
    dateToSearch: Date,
    hasEnabledMetric: (value: Array<BillingMetrics>) => boolean,
  ): Promise<void> => {
    try {
      setIsAdvancedDeuLoading(true);

      const deuAiEnabled = await FeatureToggleService.isFeatureToggleEnabled(DEU_AI_ENABLED);
      setIsDeuAiEnabled(deuAiEnabled);
      const thirdMetric = deuAiEnabled ? BillingMetrics.dailyEngagedUserAI : BillingMetrics.userConversation;

      const dailyMetricsIds = [
        hasEnabledMetric([BillingMetrics.userConversation]) && thirdMetric,
        hasEnabledMetric([BillingMetrics.prospectConversation]) && BillingMetrics.prospectConversation,
        BillingMetrics.dailyEngagedUser,
      ]
        .filter(Boolean)
        .join(';');

      const monthString = `${dateToSearch.getFullYear()}-${getMonthTwoDigits(dateToSearch)}`;
      const storageType = (await FeatureToggleService.isDatabricksStorageTypeEnabled(BillingMetrics.dailyEngagedUser))
        ? 1
        : 0;

      const apiDataDaily =
        hasEnabledMetric([BillingMetrics.userConversation, BillingMetrics.prospectConversation])
          ? await billingAdvancedDailyEngagedUserApi.getDailyAdvancedDailyEngagedUserConsumption(
              tenant.info.id,
              dailyMetricsIds,
              monthString,
              -1,
              storageType,
            )
          : [initialDailyConsumption];

      const compiledAdvancedDeuConsumption = apiDataDaily.map((item) => new CompiledAdvancedDailyEngagedUserConsumption(item));
      const statusDaily =  billingAdvancedDailyEngagedUserApi.getStatus() === RequestStatus.SUCCESS;

      const totalMetricsIds = [dailyMetricsIds, BillingMetrics.activeMessage].join(';');

      const apiDataTotal =
        hasEnabledMetric([BillingMetrics.userConversation, BillingMetrics.prospectConversation])
          ? await billingAdvancedDailyEngagedUserApi.getTotalAdvancedDailyEngagedUserConsumption(
              tenant.info.id,
              monthString,
              totalMetricsIds,
              storageType,
            )
          : initialTotalConsumption;

      const compiledTotalAdvancedDeuConsumption = new CompiledTotalAdvancedDailyEngagedUserConsumption(apiDataTotal);
      const statusTotal =  billingAdvancedDailyEngagedUserApi.getStatus() === RequestStatus.SUCCESS;

      setCompiledAdvancedDailyEngagedUserDataState({
        compiledAdvancedDailyEngagedUserConsumption: compiledAdvancedDeuConsumption,
        compiledTotalAdvancedDailyEngagedUserConsumption: compiledTotalAdvancedDeuConsumption,
        compiledContainsDataOnSelectedMonth: statusDaily && statusTotal,
      });

      if (billingAdvancedDailyEngagedUserApi.getStatus() === RequestStatus.SUCCESS) {
        setAdvancedDeuStatus(getMetricStatus(UNLIMITED_PLAN_METRIC));
      } else {
        setAdvancedDeuStatus(getMetricStatus(METRIC_NOT_REGISTERED));
      }
    } finally {
      setIsAdvancedDeuLoading(false);
    }
  }, [tenant.info.id, billingAdvancedDailyEngagedUserApi]);

  const contextValue = useMemo(
    () => ({
      compiledAdvancedDailyEngagedUserDataState,
      consolidatedAdvancedDeuApiRequisition,
      isAdvancedDeuLoading,
      advancedDeuStatus,
      advancedDeuMetricIds,
      isDeuAiEnabled,
    }),
    [
      compiledAdvancedDailyEngagedUserDataState,
      consolidatedAdvancedDeuApiRequisition,
      isAdvancedDeuLoading,
      advancedDeuStatus,
      advancedDeuMetricIds,
      isDeuAiEnabled,
    ],
  );

  return (
    <AdvancedDeuConsumptionContext.Provider value={contextValue}>{children}</AdvancedDeuConsumptionContext.Provider>
  );
};

export function useAdvancedDeuConsumptionContext(): AdvancedDeuConsumptionContextData {
  const context = React.useContext(AdvancedDeuConsumptionContext);

  if (!context) {
    throw new Error(
      'useAdvancedDeuConsumptionContext context must be used within an AdvancedDeuConsumptionContextProvider',
    );
  }

  return context;
}
